/**
 * Reusable phone number utilities using google-libphonenumber
 * Used across RMC order intake components for consistent phone number handling
 */
import {PhoneNumber} from '@hconnect/tuc-processes/types'
import {PhoneNumberUtil} from 'google-libphonenumber'
import _ from 'lodash'

const phoneNumberUtil = PhoneNumberUtil.getInstance()

/**
 * Convert PhoneNumber object to string for display
 * Fixed to handle null dialingCode properly
 */
export const phoneNumberToString = (phoneNumber?: PhoneNumber): string => {
  if (!phoneNumber) {
    return ''
  }
  // Handle null dialingCode case - don't show "null"
  if (!phoneNumber.dialingCode || !phoneNumber.number) {
    return phoneNumber.number || ''
  }
  return `${phoneNumber.dialingCode}${phoneNumber.number}`
}

/**
 * Parse phone number string into PhoneNumber object
 */
export const parsePhoneNumber = (phoneNumber: string, defaultCountry: string): PhoneNumber => {
  const country: string = (() => {
    try {
      const number = phoneNumberUtil.parse(phoneNumber)
      const regionCode = phoneNumberUtil.getRegionCodeForNumber(number)
      return regionCode || defaultCountry
    } catch (err) {
      return defaultCountry
    }
  })()

  const parsedNumber = (() => {
    if (isPhoneNumberValid(phoneNumber, country)) {
      return phoneNumberUtil.parse(phoneNumber, country)
    }
    return null
  })()

  const dialingCode = (() => {
    if (parsedNumber) {
      return `+${parsedNumber.getCountryCode()}`
    }
    // Try to get dialing code from regions - this would need regions import
    return null
  })()

  const number = (() => {
    if (parsedNumber) {
      return `${parsedNumber.getNationalNumber()}`
    }
    const trimmedPhoneNumber = _.isString(phoneNumber) ? phoneNumber.replace(/ /g, '') : ''
    if (dialingCode) {
      return trimmedPhoneNumber.replace(dialingCode, '')
    }
    return trimmedPhoneNumber
  })()

  return {
    dialingCode,
    number,
    country
  }
}

/**
 * Helper function to validate phone number format
 */
export const isPhoneNumberValid = (phoneNumber: string, country?: string): boolean => {
  try {
    const parsed = phoneNumberUtil.parse(phoneNumber, country)
    return phoneNumberUtil.isValidNumber(parsed)
  } catch (err) {
    return false
  }
}
